import { create } from 'zustand';
import { useProjectStore } from './projectStore.js';

/**
 * @typedef {import('../types/DeviceDescriptor').DeviceDescriptor} DeviceDescriptor
 */
const IS_DESKTOP = import.meta.env.VITE_IS_DESKTOP === 'true';
const DESKTOP_DEFAULT_PORT = import.meta.env.DEV ? '8000' : '8765';
const BACKEND_URL = `${import.meta.env.VITE_BACKEND_URL || `http://127.0.0.1:${DESKTOP_DEFAULT_PORT}`}/desktop`;
const MAX_AGE_SECONDS = 7200; // 2 hours

let eventSource = null;

// Buffer incoming device stream points and flush them to the store in one batched update at a
// fixed cadence, so a burst of N messages costs one re-render, not N.
let pendingSensorPoints = [];
let sensorFlushTimer = null;
const SENSOR_FLUSH_INTERVAL_MS = 100;

// Helper to compute Graph-compatible chamberAData
const computeChamberAData = (chamberData, selectedChamber) => {
  const ch = chamberData[selectedChamber];
  if (!ch || !ch.times.length) return null;
  return {
    timeSeries: ch.times,
    oxygenSeries: ch.values,
    dataFrequency: 10,
    fileName: `Chamber ${selectedChamber}`,
  };
};

export const useDeviceStore = create((set, get) => ({
  // Connection default to the device's direct WebSocket URL.
  endpointUrl: 'ws://10.12.15.20:5002/ws',
  connectionState: 'disconnected', // disconnected | connecting | connected | error
  connectionError: null,

  // Device info
  /** @type {DeviceDescriptor | null} */
  deviceDescriptor: null,

  chamberData: {},

  // Tab selection — defaults to the first chamber so the device view opens
  // on a chamber's empty state ("Start Measurement") rather than the log tab.
  selectedTab: 'chamber-1',

  // Epoch ms when the current measurement started; null while no measurement is running.
  measurementStartedAt: null,

  // Graph-compatible interface (kept in state for Zustand reactivity)
  chamberAData: null,
  chamberBData: null,
  chamberAMarks: [],
  chamberBMarks: [],
  isLoading: false,
  error: null,
  currentFileMeta: { file_name: 'Device', data_frequency: 10 },

  // Actions
  setEndpointUrl: (url) => set({ endpointUrl: url }),

  setConnectionError: (error) => set({ connectionError: error }),

  setSelectedTab: (id) =>
    set((state) => ({
      selectedTab: id,
      chamberAData: computeChamberAData(state.chamberData, id),
    })),

  connect: (projectId) => {
    const { endpointUrl } = get();

    // Close existing
    if (eventSource) {
      eventSource.close();
    }

    set({ connectionState: 'connecting', connectionError: null, measurementStartedAt: null });

    const sseUrl = `${BACKEND_URL}/device/sse?ws_url=${encodeURIComponent(endpointUrl)}&project_id=${encodeURIComponent(projectId)}`;
    eventSource = new EventSource(sseUrl);

    eventSource.addEventListener('status', (e) => {
      const data = JSON.parse(e.data);
      set((state) => ({
        connectionState: data.state,
        connectionError: data.message || data.reason || null,
        // Start the clock when the measurement begins; clear it otherwise.
        measurementStartedAt:
          data.state === 'connected' ? (state.measurementStartedAt ?? Date.now()) : null,
      }));
    });

    eventSource.addEventListener('sensor', (e) => {
      const data = JSON.parse(e.data);
      get().addSensorPoint(data);
    });

    eventSource.addEventListener('error', (e) => {
      try {
        const data = JSON.parse(e.data);
        console.warn('Device error:', data);
      } catch {
        set({ connectionState: 'error', connectionError: 'SSE connection failed' });
      }
    });

    eventSource.onerror = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        set({
          connectionState: 'disconnected',
          selectedTab: 'chamber-1',
          measurementStartedAt: null,
        });
      }
    };
  },

  disconnect: () => {
    // Stop the recording, then refresh the project's file list so the freshly
    // written .dld9 appears without a manual refresh. The stop response only
    // resolves once the recording is finalized on disk, so we refetch after it
    // settles. (Project files are fetched on explicit actions, not polled,
    // because it reads the git repo — this stop is one such explicit action.)
    fetch(`${BACKEND_URL}/device/measurement/stop`, { method: 'POST' })
      .catch(() => {})
      .finally(() => {
        useProjectStore.getState().fetchProjectFiles();
      });

    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    set({
      connectionState: 'disconnected',
      connectionError: null,
      selectedTab: 'chamber-1',
      measurementStartedAt: null,
    });
  },

  // Buffer the incoming point and schedule a batched flush, so a burst of messages from the device doesn't block the UI thread.
  addSensorPoint: (data) => {
    pendingSensorPoints.push(data);
    if (sensorFlushTimer === null) {
      sensorFlushTimer = setTimeout(() => {
        sensorFlushTimer = null;
        get().flushSensorPoints();
      }, SENSOR_FLUSH_INTERVAL_MS);
    }
  },

  // Apply all buffered points in a single store update.
  flushSensorPoints: () => {
    const points = pendingSensorPoints;
    if (points.length === 0) return;
    pendingSensorPoints = [];

    set((state) => {
      const chamberData = { ...state.chamberData };
      const touchedSelected = points.some((p) => p.referenceId === state.selectedTab);

      for (const data of points) {
        const chamberId = data.referenceId;
        const time = data.unixTimestampMs / 1000;
        const value = data.value;

        // Copy-on-write: clone each chamber once per flush, then mutate it.
        let chamber = chamberData[chamberId];
        if (!chamber || chamber === state.chamberData[chamberId]) {
          chamber = chamber
            ? {
                times: [...chamber.times],
                values: [...chamber.values],
                startIndex: chamber.startIndex,
              }
            : { times: [], values: [], startIndex: 0 };
          chamberData[chamberId] = chamber;
        }

        chamber.times.push(time);
        chamber.values.push(value);

        const cutoff = time - MAX_AGE_SECONDS;
        while (
          chamber.startIndex < chamber.times.length &&
          chamber.times[chamber.startIndex] < cutoff
        ) {
          chamber.startIndex++;
        }
        if (chamber.startIndex > 1000) {
          chamber.times = chamber.times.slice(chamber.startIndex);
          chamber.values = chamber.values.slice(chamber.startIndex);
          chamber.startIndex = 0;
        }
      }

      let newChamberAData = state.chamberAData;
      if (touchedSelected) {
        newChamberAData = computeChamberAData(chamberData, state.selectedTab);
      }

      return { chamberData, chamberAData: newChamberAData };
    });
  },

  clearData: () => {
    if (sensorFlushTimer !== null) {
      clearTimeout(sensorFlushTimer);
      sensorFlushTimer = null;
    }
    pendingSensorPoints = [];
    set({ chamberData: {}, chamberAData: null });
  },

  /** @returns {Promise<DeviceDescriptor>} */
  fetchDescriptor: async (relayIp = '', relayUsername = '', relayPassword = '', localIp = '') => {
    const { endpointUrl } = get();
    // Convert ws://host:port/ws to http://host:port
    const baseUrl = endpointUrl
      .replace(/^ws:/, 'http:')
      .replace(/^wss:/, 'https:')
      .replace(/\/ws\/?$/, '');

    const params = new URLSearchParams({
      base_url: baseUrl,
      relay_ip: relayIp,
      relay_username: relayUsername,
      relay_password: relayPassword,
      local_ip: localIp,
    });

    try {
      const res = await fetch(`${BACKEND_URL}/device/descriptor?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      /** @type {DeviceDescriptor} */
      const descriptor = await res.json();
      set({ deviceDescriptor: descriptor });
      return descriptor;
    } catch (e) {
      console.error('Failed to fetch descriptor:', e);
      throw e;
    }
  },
}));
